import {isExternalLink} from '../../utils/utils'
import {sanitizeAnchorTarget} from '../utils/utils'
import useNavigation from '../../hooks/use-navigation'
import {convertToRelativePath} from '../../utils/url'
import {useMemo} from 'react'

const useCTAAction = ({action, openInNewTab = false, onClick: _onClick} = {}) => {
    // use relative links instead for DEV/STG/PRD to handle mobify and local development environments
    const linkHref = useMemo(() => {
        if (action?.type === 'anchor') {
            return sanitizeAnchorTarget(action.value)
        }
        return convertToRelativePath(action?.value)
    }, [action?.type, action?.value])
    const navigate = useNavigation()

    const onClick = (e) => {
        // Call any provided onClick handler first
        if (_onClick) {
            _onClick(e)
            // and short-circuit if event was handled/prevented
            if (e.defaultPrevented) {
                return
            }
        }

        if (!linkHref) {
            e.preventDefault()
            return
        }

        // Handle different action types
        switch (action.type) {
            case 'url':
            case 'anchor':
                // For url and anchor types, use default browser handling
                return

            case 'product':
            case 'category':
            case 'content': {
                if (isExternalLink(linkHref)) {
                    // For external links, fallback to default browser handling
                    return
                }

                if (!openInNewTab) {
                    e.preventDefault()
                    // For internal links, use client-side navigation
                    navigate(linkHref)
                }
                return
            }

            default:
                // Fallback: leave default handling for unknown types
                return
        }
    }

    const target = openInNewTab ? '_blank' : undefined
    const rel = openInNewTab ? 'noopener noreferrer' : undefined

    return linkHref
        ? {
              href: linkHref,
              onClick,
              target,
              rel,
              as: 'a'
          }
        : {
              onClick,
              as: 'button'
          }
}

export default useCTAAction
